import { theme as defaultChakra } from "@chakra-ui/core";
import { css, keyframes } from "styled-components";
import { colors as themeColors } from "./theme/colors";

export const colors = {
  ...themeColors,
  white: "#FFFFFF",
  black: "#303030",
};

export const weights = {
  light: "300",
  normal: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
};

export const zIndex = {
  low: 0,
  carouselGradient: 1,
  dropdown: 1,
  tooltip: 10,
  header: 12,
  footer: 9,
  modalOverlay: 11,
  modal: 12,
  loading: 20,
};

export const borderRadius = {
  extraSmall: "2px",
  small: "6px",
  regular: "6px",
  large: "8px",
  extraLarge: "12px",
  superLarge: "28px",
  modal: "16px",
  uber: "32px",
};
export const borderThickness = "2px";

export const borders = {
  radiusExtraSmall: borderRadius.extraSmall,
  radiusSmall: borderRadius.small,
  radius: borderRadius.regular,
  radiusLarge: borderRadius.large,
  radiusExtraLarge: borderRadius.extraLarge,
  radiusSuperLarge: borderRadius.superLarge,
  radiusModal: borderRadius.modal,
  radiusUber: borderRadius.uber,
  thickness: borderThickness,
};

// Same breakpoints as styled-system defaults
export const breakpoints = {
  small: "40em", // 640px
  medium: "52em", // 832px
  large: "64em", // 1024px
  extraLarge: "75em", // 1200px
};

// Iterate through the sizes and create a media template
export const media = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${breakpoints[label]}) {
      ${css(...args)}
    }
  `;

  return acc;
}, {});

export const opacityPulse = keyframes`
  0%, 100% {
    opacity: 1;
  }

  50% {
    opacity: 0.1;
  }
`;

export const jolt = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.12);
  }

  50% {
    transform: scale(1.02);
    box-shadow: 0px 8px 20px rgba(0, 0, 0, 0.05);
  }
  75% {
   transform: scale(0.99);
  }
`;

export const fonts = {
  body: '"Open Sans", system-ui, sans-serif',
  heading: '"Obviously", system-ui, sans-serif',
  mono: '"Courier New", Courier, monospace',
};

export const fontSizes = {
  xs: "12px",
  sm: "14px",
  md: "16px",
  lg: "18px",
  xl: "20px",
  "2xl": "24px",
  "3xl": "28px",
  "4xl": "36px",
  "5xl": "44px",
  "6xl": "56px",
  "7xl": "72px",
};

// We can use Chakras default spacing values. This is for reference only.
// https://chakra-ui.com/theme#spacing

// export const space = {
//   px: "1px",       // 1px
//   "0": "0",        // 0px
//   "1": "0.25rem",  // 4px
//   "2": "0.5rem",   // 8px
//   "3": "0.75rem",  // 12px
//   "4": "1rem",     // 16px
//   "5": "1.25rem",  // 20px
//   "6": "1.5rem",   // 24px
//   "7": "1.75rem",  // 28px
//   "8": "2rem",     // 32px
//   "9": "2.25rem",  // 36px
//   "10": "2.5rem",  // 40px
//   "11": "2.75rem",  // 44px
//   "12": "3rem",    // 48px
//   "14": "3.5rem",  // 56px
//   "15": "3.75rem", // 60px
//   "16": "4rem",    // 64px
//   "18": "4.5rem",    // 72px
//   "20": "5rem",    // 80px
//   "22": "5.5rem",  // 88px
//   "24": "6rem",    // 96px
//   "28": "7rem",    // 112px
//   "32": "8rem",    // 128px
//   "40": "10rem",   // 160px
//   "48": "12rem",   // 192px
//   "56": "14rem",   // 224px
//   "64": "16rem",   // 256px
//   "72": "18rem",   // 288px
// };

const additionalSpaces = {
  "7": "1.75rem", // 28px
  "9": "2.25rem", // 36px
  "11": "2.75rem", // 44px
  "14": "3.5rem", // 56px
  "15": "3.75rem", // 60px
  "18": "4.5rem", // 72px
  "22": "5.5rem", // 88px
  "28": "7rem", // 112px
  "72": "18rem", // 288px
};

export const systemTheme = {
  colors,
  breakpoints: Object.values(breakpoints),
  fontWeights: weights,
  radii: {
    regular: borders.radiusLarge,
    large: borders.radiusLarge,
  },
  zIndices: zIndex,
};

export const chakraTheme = {
  ...defaultChakra,
  colors: {
    ...themeColors,
  },
  breakpoints: systemTheme.breakpoints,
  fontSizes,
  letterSpacings: {
    ...defaultChakra.letterSpacings,
    homepageCta: "0.171875em",
  },
  shadows: {
    ...defaultChakra.shadows,
    inner: "inset 0px 2px 3px rgba(0, 0, 0, 0.12)",
    activeToggle:
      "0px 4px 6px rgba(0, 0, 0, 0.25), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    heavy: "0 4px 16px rgba(0, 0, 0, 0.48)",
    inputUp: "0px 4px 8px rgba(0, 0, 0, 0.16)",
    inputDown: "0px 2px 2px rgba(0, 0, 0, 0.24)",
    wizardFooter: "0px -4px 8px rgba(18, 52, 86, 0.08)",
    lg: "0 8px 16px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    modal: "0px 16px 48px rgba(0, 0, 0, 0.4)",
  },
  sizes: {
    ...defaultChakra.sizes,
    ...additionalSpaces,
  },
  space: {
    ...defaultChakra.space,
    ...additionalSpaces,
  },
  radii: {
    ...defaultChakra.radii,
    xl: "0.75rem",
  },
};

export const primaryShadeForColor = {
  blue: 500,
  cyan: 500,
  gray: 500,
  green: 500,
  navy: 500,
  orange: 500,
  pink: 500,
  red: 500,
  teal: 500,
};

export type ThemeColor = keyof typeof colors;

export const variantColorToShade = (color: ThemeColor) => {
  if (
    (color && color.search(/.\d/) > -1) ||
    ["white", "black"].includes(color)
  ) {
    return color;
  }

  return `${color}.${primaryShadeForColor[color]}`;
};
